import { useRef, useState } from "react";
import { GstRestS } from "../../../rest/gst-rest-service";
import {
  GstJobUpdate,
  useGstReconSocket,
} from "../../../components/Hooks/useGstReconSocket";
import { trackGstButtonClicked, trackGstErrorShown } from "../gstAmplitude";
import { GSTUploadListModal, DEFAULT_IMPORT_TIMEOUT_MS } from "../../../components/GSTConnection";

interface UploadGstinListModalProps {
  onClose: () => void;
  onImported: (records?: Array<{ gstin: string; username: string; state?: string }>) => void;
}

export default function UploadGstinListModal({
  onClose,
  onImported,
}: UploadGstinListModalProps) {
  const [importing, setImporting] = useState(false);
  const [readError, setReadError] = useState(false);
  const [fileError, setFileError] = useState("");
  // jobId of the run_fetch_gstin job we're waiting on; null = not waiting
  const jobIdRef = useRef<string | number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const failImport = (message?: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    jobIdRef.current = null;
    setImporting(false);
    setReadError(true);
    trackGstErrorShown(
      "gstin_list_upload",
      "file_read_failed",
      message || "Couldn't read your file.",
      "file"
    );
  };

  // The upload response only confirms the job started ({ uploaded: [{ jobId }] });
  // the outcome arrives as a run_fetch_gstin GST_RECON_JOB_UPDATE socket event.
  useGstReconSocket((update: GstJobUpdate) => {
    if (update.jobType !== "run_fetch_gstin") return;
    if (jobIdRef.current == null) return;
    if (String(update.jobId) !== String(jobIdRef.current)) return;

    if (
      update.status === "completed" ||
      update.status === "completed_with_errors"
    ) {
      const jobData = update.data as any;
      const saved = jobData?.savedGstins?.length ?? 0;
      if (saved > 0) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        jobIdRef.current = null;
        onImported(jobData?.records);
        return;
      }
      // File parsed but nothing usable — e.g. every row missing a username
      const reason = jobData?.skippedRows?.[0]?.reason;
      failImport(
        reason === "Missing username"
          ? "No accounts imported — the rows are missing usernames."
          : "No accounts could be imported from this file."
      );
      return;
    }

    if (update.status === "failed") {
      failImport(update.error?.message);
    }
    // pending / running — keep the loader going
  });

  const handleImport = async (file: File) => {
    if (importing) return;
    setImporting(true);
    setReadError(false);
    setFileError("");
    try {
      const res = await GstRestS.uploadCredentialsList(file);
      const jobId = res?.data?.uploaded?.[0]?.jobId;
      if (res?.success && jobId != null) {
        // Upload accepted — keep the loader on and wait for the socket update
        jobIdRef.current = jobId;
        timeoutRef.current = setTimeout(() => {
          failImport("The import is taking too long. Please try again.");
        }, DEFAULT_IMPORT_TIMEOUT_MS);
        return;
      }
      failImport(res?.data?.failed?.[0]?.error || res?.message);
    } catch {
      failImport();
    }
  };

  return (
    <GSTUploadListModal
      importing={importing}
      readError={readError}
      fileError={fileError}
      onClose={() => {
        trackGstButtonClicked("Close", "upload_list_modal");
        onClose();
      }}
      onImport={(file) => {
        trackGstButtonClicked("Import Accounts", "upload_list_modal");
        handleImport(file);
      }}
      onClearError={() => {
        setReadError(false);
        setFileError("");
      }}
    />
  );
}
